import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { MedicationFormData } from '@/components/medication/types/medication';
import AnimatedContainer from '@/components/ui-elements/AnimatedContainer';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface CycleStepProps {
  formData: MedicationFormData;
  updateFormData: (fieldName: keyof MedicationFormData, value: any) => void;
}

/**
 * Step 3: Configure medication cycle information
 */
const CycleStep: React.FC<CycleStepProps> = ({ formData, updateFormData }) => {
  const handleDateChange = (fieldName: 'cycleStartDate' | 'cycleEndDate', date: Date | undefined) => {
    if (date) {
      updateFormData(fieldName, format(date, 'yyyy-MM-dd'));
    } else {
      updateFormData(fieldName, undefined);
    }
  };

  return (
    <AnimatedContainer animation="fade-in" className="space-y-6">
      <div className="flex items-center justify-between">
        <Label htmlFor="has-cycle" className="text-sm font-medium">
          Does this medication follow a cycle?
        </Label>
        <Switch
          id="has-cycle"
          checked={formData.hasCycle}
          onCheckedChange={(checked) => updateFormData('hasCycle', checked)}
        />
      </div>

      {formData.hasCycle && (
        <div className="space-y-6 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cycle-weeks-on" className="text-sm font-medium">
                Weeks On <span className="text-red-500">*</span>
              </Label>
              <Input
                id="cycle-weeks-on"
                type="number"
                min={1}
                value={formData.cycleWeeksOn}
                onChange={(e) => updateFormData('cycleWeeksOn', parseInt(e.target.value) || 1)}
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cycle-weeks-off" className="text-sm font-medium">
                Weeks Off <span className="text-red-500">*</span>
              </Label>
              <Input
                id="cycle-weeks-off"
                type="number"
                min={1}
                value={formData.cycleWeeksOff}
                onChange={(e) => updateFormData('cycleWeeksOff', parseInt(e.target.value) || 1)}
                className="w-full"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="cycle-start-date" className="text-sm font-medium">
              Start Date
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="cycle-start-date"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.cycleStartDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.cycleStartDate ? (
                    format(new Date(formData.cycleStartDate), 'PPP')
                  ) : (
                    <span>Select start date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.cycleStartDate ? new Date(formData.cycleStartDate) : undefined}
                  onSelect={(date) => handleDateChange('cycleStartDate', date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="cycle-end-date" className="text-sm font-medium">
              End Date (Optional)
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="cycle-end-date"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.cycleEndDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.cycleEndDate ? (
                    format(new Date(formData.cycleEndDate), 'PPP')
                  ) : (
                    <span>Select end date (optional)</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.cycleEndDate ? new Date(formData.cycleEndDate) : undefined}
                  onSelect={(date) => handleDateChange('cycleEndDate', date)}
                  initialFocus
                  disabled={(date) => {
                    // Disable dates before start date
                    if (!formData.cycleStartDate) return false;
                    const startDate = new Date(formData.cycleStartDate);
                    return date < startDate;
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
          
          {formData.cycleWeeksOn > 0 && formData.cycleWeeksOff > 0 && (
            <div className="mt-4 p-4 bg-muted/40 rounded-lg">
              <p className="text-sm font-medium mb-2">Cycle Summary:</p>
              <p className="text-sm">
                This medication will be taken for {formData.cycleWeeksOn} {formData.cycleWeeksOn === 1 ? 'week' : 'weeks'}, 
                followed by {formData.cycleWeeksOff} {formData.cycleWeeksOff === 1 ? 'week' : 'weeks'} rest, 
                in a repeating cycle.
              </p>
            </div>
          )}
        </div>
      )}
    </AnimatedContainer>
  );
};

export default CycleStep;
